/**
 * The vocabulary and the pure decisions behind a company's "Sales & Billing"
 * section — the four document kinds raised against an account, how each is
 * named, where its list page lives, and how a row of each reads in a compact
 * table.
 *
 * ⛔ **NOTHING HERE FETCHES, RENDERS OR SUMS.** No React, no `api`, no hooks.
 * `CompanyDocumentsHistory.tsx` owns the reads and the screen; this module
 * answers *given a record, what does its row say* and *given four counts, what
 * does the folded header say*. Money is carried through as the decimal string
 * the server sent, beside its currency code, and is never added across rows:
 * a company's documents are routinely in several currencies and one figure
 * over them is wrong by construction.
 *
 * ## The list links carry `?companyId=N`, and that was VERIFIED per page
 *
 * All four list pages read `companyId` out of their query string and render it
 * as a filter badge (2026-10-11: `DealsPage` parses it from `searchParams`,
 * `QuotesPage` and `InvoicesPage` carry it in their URL key lists,
 * `CreditNotesPage` lists it among its URL keys and its Company column
 * deliberately declines a `param` so the link is the key's only writer). A
 * link filtering on a key the page does not read would land on the unfiltered
 * list and look exactly like a filter that worked — re-check the page before
 * adding a fifth kind here.
 */

import type { CreditNote, Deal, Invoice, Quote } from '@/lib/types';
import { dealValue } from '@/lib/deals';
import { creditNoteRef } from '@/lib/credit-notes';

export type CompanyDocumentKind = 'deals' | 'quotes' | 'invoices' | 'creditNotes';

/** Tab order — the order the business raises them in. */
export const COMPANY_DOCUMENT_KINDS: readonly CompanyDocumentKind[] = [
  'deals',
  'quotes',
  'invoices',
  'creditNotes',
];

export interface CompanyDocumentLabels {
  /** The tab label — `Deals`, `Credit notes`. */
  readonly tab: string;
  /** One of them, for an empty state — `deal`, `credit note`. */
  readonly singular: string;
  /** Several, for a count — `deals`, `credit notes`. */
  readonly plural: string;
  /** The heading over the identifying column. */
  readonly nameHeading: string;
  /** The heading over the stage/status column. */
  readonly stateHeading: string;
  /** The heading over the money column. */
  readonly amountHeading: string;
  /** The heading over the key date column. */
  readonly dateHeading: string;
  /** What is in the table when it is empty — a fact, never a promise. */
  readonly emptyTitle: string;
  readonly emptyDescription: string;
  /** The list page, before its query string. */
  readonly listPath: string;
}

export const COMPANY_DOCUMENT_LABELS: Readonly<Record<CompanyDocumentKind, CompanyDocumentLabels>> =
  {
    deals: {
      tab: 'Deals',
      singular: 'deal',
      plural: 'deals',
      nameHeading: 'Deal',
      stateHeading: 'Stage',
      amountHeading: 'Value',
      dateHeading: 'Closing',
      emptyTitle: 'No deals',
      emptyDescription: 'Nothing has been raised against this company.',
      listPath: '/deals',
    },
    quotes: {
      tab: 'Quotes',
      singular: 'quote',
      plural: 'quotes',
      nameHeading: 'Quote',
      stateHeading: 'Stage',
      amountHeading: 'Total',
      dateHeading: 'Issued',
      emptyTitle: 'No quotes',
      emptyDescription: 'Nothing has been quoted to this company.',
      listPath: '/quotes',
    },
    invoices: {
      tab: 'Invoices',
      singular: 'invoice',
      plural: 'invoices',
      nameHeading: 'Invoice',
      stateHeading: 'Status',
      amountHeading: 'Total',
      dateHeading: 'Billed',
      emptyTitle: 'No invoices',
      emptyDescription: 'Nothing has been billed to this company.',
      listPath: '/invoices',
    },
    creditNotes: {
      tab: 'Credit notes',
      singular: 'credit note',
      plural: 'credit notes',
      nameHeading: 'Credit note',
      stateHeading: 'Status',
      amountHeading: 'Total',
      dateHeading: 'Raised',
      emptyTitle: 'No credit notes',
      emptyDescription: 'Nothing has been credited to this company.',
      listPath: '/credit-notes',
    },
  };

/** The record page for one document. */
export function companyDocumentHref(kind: CompanyDocumentKind, id: number): string {
  return `${COMPANY_DOCUMENT_LABELS[kind].listPath}/${id}`;
}

/** The list page, filtered to this company — every one of the four reads the key. */
export function companyDocumentListHref(kind: CompanyDocumentKind, companyId: number): string {
  return `${COMPANY_DOCUMENT_LABELS[kind].listPath}?companyId=${companyId}`;
}

/** `View all 40 invoices` / `View all 1 credit note`. */
export function viewAllLabel(kind: CompanyDocumentKind, total: number): string {
  const labels = COMPANY_DOCUMENT_LABELS[kind];
  return `View all ${total} ${total === 1 ? labels.singular : labels.plural}`;
}

/**
 * The folded header's one-line summary — `3 deals · 12 quotes · 40 invoices ·
 * 2 credit notes`.
 *
 * ⚠️ **A `null` count is OMITTED, never printed as `0`.** `null` means the read
 * has not landed or failed — most often the credit-notes read refused to a
 * reader without the grant — and a `0` there would claim the company has no
 * credit notes on the strength of not having been allowed to look. With every
 * count `null` there is nothing honest to say, so the answer is `null` and the
 * caller renders no summary at all.
 */
export function companyDocumentsSummary(
  counts: Partial<Record<CompanyDocumentKind, number | null>>,
): string | null {
  const parts: string[] = [];
  for (const kind of COMPANY_DOCUMENT_KINDS) {
    const count = counts[kind];
    if (count === null || count === undefined) continue;
    const labels = COMPANY_DOCUMENT_LABELS[kind];
    parts.push(`${count} ${count === 1 ? labels.singular : labels.plural}`);
  }
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * What every kind's row has in common. The stage or status badge is rendered by
 * the component from the record itself (each kind has its own badge component
 * and its own flag semantics), so it is deliberately NOT flattened here.
 */
export interface CompanyDocumentRow {
  readonly id: number;
  /** The identifying text — a deal's name, a document's subject. */
  readonly name: string;
  /**
   * A second identifier under the name — the Xero number on a quote, invoice or
   * credit note. `null` where there is none, which on a quote is most of them:
   * `quoteNumber` is NULL on every quote today and is not read here.
   */
  readonly reference: string | null;
  /**
   * The money figure as the server's decimal string, or `null` where the
   * record carries none. Rendered beside {@link currency}, always.
   */
  readonly amount: string | null;
  readonly currency: string | null;
  /** The key date as a plain `YYYY-MM-DD` or an ISO instant, or `null`. */
  readonly date: string | null;
  /**
   * The date that places the document in a cycle (see `cycleOfDate`). Per kind:
   * deal → closing date, else created date; quote → issue date; invoice → bill
   * date; credit note → credit note date, else created date. `null` = no cycle.
   */
  readonly cycleDate: string | null;
  /** The stage or status master row, for the column filter — `null` when unset. */
  readonly state: { readonly id: number; readonly name: string } | null;
}

/**
 * ⚠️ `dealValue()`, never `deal.amount` alone — a deal with lines reports the
 * lines' total and the typed amount is only the fallback.
 */
export function dealRow(deal: Deal): CompanyDocumentRow {
  return {
    id: deal.id,
    name: deal.dealName,
    reference: null,
    amount: dealValue(deal),
    currency: deal.currency,
    date: deal.closingDate,
    cycleDate: deal.closingDate ?? deal.createdAt,
    state: deal.stage,
  };
}

export function quoteRow(quote: Quote): CompanyDocumentRow {
  return {
    id: quote.id,
    name: quote.subject,
    reference: quote.xeroQuoteNumber,
    amount: quote.grandTotal,
    currency: quote.currency,
    date: quote.issueDate,
    cycleDate: quote.issueDate,
    state: quote.stage,
  };
}

export function invoiceRow(invoice: Invoice): CompanyDocumentRow {
  return {
    id: invoice.id,
    name: invoice.subject,
    reference: invoice.xeroInvoiceNumber,
    amount: invoice.grandTotal,
    currency: invoice.currency,
    date: invoice.billDate,
    cycleDate: invoice.billDate,
    state: invoice.status,
  };
}

/**
 * `creditNoteRef()` is the Xero number where there is one and the subject
 * otherwise, so the name slot already carries the reference; the secondary
 * slot shows the subject only when the two differ.
 */
export function creditNoteRow(note: CreditNote): CompanyDocumentRow {
  const ref = creditNoteRef(note);
  return {
    id: note.id,
    name: ref,
    reference: ref === note.subject ? null : note.subject,
    amount: note.grandTotal,
    currency: note.currency,
    date: note.creditNoteDate ?? note.createdAt,
    cycleDate: note.creditNoteDate ?? note.createdAt,
    state: note.status,
  };
}

/* --- client-side column filtering ------------------------------------------ */

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

/**
 * Compares two decimal strings EXACTLY (BigInt, no float), `null` when either
 * is not a plain decimal. Used only to test a figure against a typed filter
 * bound — never to add figures, and never across a conversion.
 */
export function compareDecimalStrings(a: string, b: string): number | null {
  const pa = DECIMAL.exec(a.trim());
  const pb = DECIMAL.exec(b.trim());
  if (!pa || !pb) return null;
  const fa = pa[3] ?? '';
  const fb = pb[3] ?? '';
  const scale = Math.max(fa.length, fb.length);
  const ua = BigInt(`${pa[1]}${pa[2]}${fa.padEnd(scale, '0')}`);
  const ub = BigInt(`${pb[1]}${pb[2]}${fb.padEnd(scale, '0')}`);
  return ua < ub ? -1 : ua > ub ? 1 : 0;
}

/**
 * Does `value` sit inside the INCLUSIVE `[min, max]` window? A `null` bound is
 * open. A `null` value matches no bounded window — a missing figure or date is
 * reached through Is empty, never through a bound. Dates are compared as bare
 * `YYYY-MM-DD` strings (an instant by its leading day); figures exactly.
 */
export function withinBounds(
  value: string | null,
  bounds: { min: string | null; max: string | null },
  kind: 'date' | 'decimal',
): boolean {
  if (bounds.min === null && bounds.max === null) return true;
  if (value === null) return false;
  const subject = kind === 'date' ? value.slice(0, 10) : value;
  const compare = (bound: string): number | null =>
    kind === 'date'
      ? subject < bound
        ? -1
        : subject > bound
          ? 1
          : 0
      : compareDecimalStrings(subject, bound);
  if (bounds.min !== null) {
    const result = compare(bounds.min);
    if (result === null || result < 0) return false;
  }
  if (bounds.max !== null) {
    const result = compare(bounds.max);
    if (result === null || result > 0) return false;
  }
  return true;
}
